import { inject, type Provider } from '@angular/core';
import { map } from 'rxjs';
import { SesionService } from '@intelishs-group/session-core';
import { coincide, proveerFuenteIrA, type IrAResultado } from '@intelishs-group/ui/ir-a';
import { EmpleadoService } from '@services/empleado.service';
import type { EmpleadoList } from '@schemas/empleado.schema';

/** El módulo de la lista y de la ficha a la que lleva el resultado. */
const MODULO = 'empleados';

/**
 * «Ir a…» (Ctrl+K) busca empleados: por nombre, clave interna (MC####), RFC,
 * CURP, puesto o área, sin acentos, y lleva a su ficha.
 *
 * ── QUIÉN LA VE ────────────────────────────────────────────────────────────
 *
 * Sólo quien tiene el módulo `empleados` (`activa`): hoy, el administrador de RH
 * y el superadministrador. Es lo que exige la API para la lista completa
 * (`GET /api/Empleado`, Admin o RH) y la misma lista que esa sesión ya ve en
 * Empleados: no enseña nada nuevo.
 *
 * El DIRECTOR no la tiene, a propósito. Su equipo se acota hoy sólo en el front
 * (la API acepta a un director en la ficha de cualquier empleado y en
 * `equipo-saldo` con las áreas que le mande el front); una búsqueda de «mi
 * equipo» se apoyaría en eso. Decidido el 2026-10-23: así se queda.
 *
 * ── DE DÓNDE ───────────────────────────────────────────────────────────────
 *
 * De `EmpleadoService.getAll()`, el mismo que usa la lista y que ya guarda la
 * respuesta en caché: unos 210 empleados, que se buscan en memoria. RFC y CURP
 * sirven para encontrar pero NO se pintan: un dato sensible no tiene por qué
 * salir en una lista, y basta con saber que coincidió.
 */
export function proveerFuenteEmpleados(): Provider {
  return proveerFuenteIrA(() => {
    const empleados = inject(EmpleadoService);
    const sesion = inject(SesionService);
    return {
      tipo: 'empleado',
      titulo: 'Empleados',
      icono: 'contacts',
      max: 8,
      activa: () => sesion.tieneModulo(MODULO),
      buscar: (_texto, palabras) =>
        empleados.getAll().pipe(
          map((lista) =>
            lista
              .filter((e) =>
                coincide(palabras, e.nombreCompleto, e.idInterno, e.rfc, e.curp, e.puesto, e.area),
              )
              // Los activos primero: a quien se busca casi siempre sigue aquí.
              .sort((a, b) => Number(b.activo) - Number(a.activo))
              .map((e) => aResultadoEmpleado(e)),
          ),
        ),
      verTodos: (texto) => `/empleados?buscar=${encodeURIComponent(texto)}`,
    };
  });
}

/** Un empleado como resultado: el nombre y, abajo, su clave, puesto y área. */
export function aResultadoEmpleado(e: EmpleadoList): IrAResultado {
  return {
    clave: String(e.id),
    texto: e.nombreCompleto,
    meta: [e.idInterno, e.puesto, e.area, e.activo ? null : 'De baja'].filter((x) => x).join(' · '),
    ruta: `/empleados/detalle/${e.id}`,
  };
}
